import React, { Key, useState } from "react"
import { Button, message } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { ITargetDataForUpdate, SB_CAMPAIGNS_STATE_FILTER } from "../types/sbCampaignApi.types"
import { useActions } from "../../../hooks/useActions"
import sbTargetApi from "../api/sbTarget.api"
import sbPlacementApi from "../api/sbPlacement.api"
import { ISbBidAdjustmentItem, ISbPlacementResult } from "../types/sbPlacementApi.types"
import { ISbTargetDataForTable, ITargetDataForTable } from "../types/sbTarget.types"
import { ISbTargetResult } from "../types/sbTargetApi.types"
import {
  computeFinalBid,
  IFinalBidTarget,
  ISbDeflationContext,
  resolveDeflationContext,
  SB_AMAZON_MIN_BID,
} from "../hooks/helpers/sbPlacementModifier.helper"
import { isEnabledTarget } from "../../../lib/bidCore"
import { buildSbCampaignsBody } from "../services/sbCampaignsBody.service"

export function buildApplySuccessMessage(targetCount: number, adjustmentCount: number): string {
  const parts: string[] = []
  if (targetCount > 0) {
    parts.push(`${targetCount} target bid${targetCount === 1 ? "" : "s"}`)
  }
  if (adjustmentCount > 0) {
    parts.push(`${adjustmentCount} placement adjustment${adjustmentCount === 1 ? "" : "s"}`)
  }
  return `Applied ${parts.join(" and ")} on Amazon`
}

export function buildTargetBidForUpdate(
  target: IFinalBidTarget,
  context: ISbDeflationContext,
  minBid: number,
  maxBid?: number
): number {
  return computeFinalBid(target, context, minBid, maxBid) ?? 0
}

export interface ISbTargetLoadState {
  targetData: ISbTargetResult[]
  pendingCampaignIds: string[]
  failedCampaignIds: string[]
}

/**
 * Campaigns whose pending placement change must NOT be applied (SB-01). A placement change
 * ships the final bids with the modifiers: it is applied only when the campaign's target load
 * is complete (fetched, not in flight, not failed) and EVERY enabled fetched target - keyword
 * or ASIN, held ones keep their current bid - has a final bid > 0 in `collected`. An empty
 * enabled list cannot prove coverage: blocked.
 */
export function findPlacementCoverageGaps({
  pendingAdjustments,
  loadState,
  collected,
}: {
  pendingAdjustments: Record<string, ISbBidAdjustmentItem>
  loadState: ISbTargetLoadState
  collected: ITargetDataForUpdate[]
}): string[] {
  const inFlight = new Set(
    [...loadState.pendingCampaignIds, ...loadState.failedCampaignIds].map(String)
  )
  const withBid = new Set(
    collected
      .filter((target) => target.bid > 0)
      .map((target) =>
        buildTargetRowKey({
          campaignId: String(target.campaignId),
          adGroupId: String(target.adGroupId || ""),
          targetId: String(target.targetId),
        })
      )
  )

  return Object.keys(pendingAdjustments).filter((campaignId) => {
    const loaded = loadState.targetData.find((c) => String(c.campaignId) === String(campaignId))
    if (inFlight.has(String(campaignId)) || !loaded) {
      return true
    }
    const enabled = loaded.targetData.filter((target) => isEnabledTarget(target.targetStatus))
    return (
      enabled.length === 0 ||
      enabled.some(
        (target) =>
          !withBid.has(
            buildTargetRowKey({
              campaignId: String(campaignId),
              adGroupId: String(target.adGroupId || ""),
              targetId: String(target.targetId),
            })
          )
      )
    )
  })
}

function buildTargetRowKey({
  campaignId,
  adGroupId,
  targetId,
}: {
  campaignId: string
  adGroupId: string
  targetId: string
}): string {
  return [campaignId, adGroupId || "", targetId].map(String).join(":")
}

export function collectTargetsForApply({
  targetDataForTable,
  selectedTargetIds,
  pendingAdjustments,
  placementData,
  minTargetCPC,
  maxTargetCPC,
}: {
  targetDataForTable: ISbTargetDataForTable[]
  selectedTargetIds: Key[]
  pendingAdjustments: Record<string, ISbBidAdjustmentItem>
  placementData: ISbPlacementResult[]
  minTargetCPC: number
  maxTargetCPC?: number
}): ITargetDataForUpdate[] {
  const selectedIds = new Set(selectedTargetIds.map(String))
  const campaignsWithPendingAdjustments = new Set(Object.keys(pendingAdjustments).map(String))
  const targetsById = new Map<string, ITargetDataForUpdate>()

  targetDataForTable.forEach((campaign) => {
    const campaignId = String(campaign.campaignId)
    const shouldApplyCampaignTargets = campaignsWithPendingAdjustments.has(campaignId)
    const deflationContext = resolveDeflationContext(campaignId, pendingAdjustments, placementData)

    campaign.targetData.forEach((target: ITargetDataForTable) => {
      if (target.excluded) {
        return
      }
      const rowKey = buildTargetRowKey({
        campaignId,
        adGroupId: String(target.adGroupId || ""),
        targetId: String(target.targetId),
      })
      if (!shouldApplyCampaignTargets && !selectedIds.has(rowKey)) {
        return
      }
      if (targetsById.has(rowKey)) {
        return
      }

      const freshBid = buildTargetBidForUpdate(target, deflationContext, minTargetCPC, maxTargetCPC)
      targetsById.set(rowKey, {
        campaignId: target.campaignId,
        targetId: target.targetId,
        adGroupId: target.adGroupId,
        oldBid: target.bid,
        // A pending placement change always ships a freshly computed final bid (never a
        // row's earlier finalBid).
        bid: shouldApplyCampaignTargets ? freshBid : (target.finalBid ?? freshBid),
        targetName: target.targetName,
      })
    })
  })

  return [...targetsById.values()]
}

interface IApplyOnAmazon {
  selectedTargetIds: Key[]
  stateFilter: SB_CAMPAIGNS_STATE_FILTER
  onApplied?: () => void
}

const ApplyOnAmazon: React.FC<IApplyOnAmazon> = ({ selectedTargetIds, stateFilter, onApplied }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const { targetDataForTable } = useTypedSelector((select) => select.sponsoredBrandsTargetForTable)
  const { targetData, pendingCampaignIds, failedCampaignIds } = useTypedSelector(
    (select) => select.sponsoredBrandsTarget
  )
  const { pendingAdjustments, placementData } = useTypedSelector(
    (select) => select.sponsoredBrandsPlacements
  )
  const maxTargetCPC = useTypedSelector(
    (select) => select.sponsoredBrands.campaignData.maxTargetCPC
  )
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)
  const {
    fetchSponsoredBrandsCampaigns,
    resetSponsoredBrandsPlacements,
    resetSponsoredBrandsTargets,
  } = useActions()

  function collectSelectedTargets(): ITargetDataForUpdate[] {
    if (!targetDataForTable) {
      return []
    }

    return collectTargetsForApply({
      targetDataForTable,
      selectedTargetIds,
      pendingAdjustments,
      placementData,
      minTargetCPC: SB_AMAZON_MIN_BID,
      maxTargetCPC,
    })
  }

  async function handleOnClick() {
    const selectedTargetForUpdate = collectSelectedTargets()
    const gaps = findPlacementCoverageGaps({
      pendingAdjustments,
      loadState: { targetData, pendingCampaignIds, failedCampaignIds },
      collected: selectedTargetForUpdate,
    })
    if (gaps.length) {
      message.error(
        `Campaign ${gaps.join(", ")}: placement modifiers are applied only together with a ` +
          "final bid for every enabled target - expand the campaign and wait for its targets to load"
      )
      return
    }
    const adjustments = Object.values(pendingAdjustments)

    if (!selectedTargetForUpdate.length && !adjustments.length) {
      message.error("Nothing to apply")
      return
    }

    setLoading(true)
    try {
      await Promise.all([
        selectedTargetForUpdate.length
          ? sbTargetApi.updateSbTarget({ targetData: selectedTargetForUpdate })
          : Promise.resolve(),
        adjustments.length
          ? sbPlacementApi.updateBidAdjustments({ adjustments })
          : Promise.resolve(),
      ])

      message.success(buildApplySuccessMessage(selectedTargetForUpdate.length, adjustments.length))
      onApplied?.()
      resetSponsoredBrandsPlacements()
      resetSponsoredBrandsTargets()
      fetchSponsoredBrandsCampaigns(
        buildSbCampaignsBody({ dateRange, isOrder, isManaged: true, stateFilter })
      )
    } catch {
      message.error("Failed to apply on Amazon")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button loading={loading} onClick={handleOnClick}>
      Apply On Amazon
    </Button>
  )
}

export default ApplyOnAmazon
